import {Tooltip} from 'platform/components';
import {HStack, Icon, Text, themeIcons} from 'platform/foundation';

import {isNotNil, omit} from 'ramda';

import type {CellRendererProps} from '../../types/CellRendererProps';
import type {CellStyle} from '../../types/CellStyle';
import type {TooltipGetter} from '../../types/TooltipGetter';
import {getCellClassName} from '../../utils/getCellClassName';
import {getStylesToRender} from '../../utils/getStylesToRender';
import {getTooltipProps} from '../../utils/getTooltipProps';
import {getMappedIconColor} from './utils/getMappedIconColor';

type IconCellRendererProps = CellRendererProps<string | null> & {
  getTooltipProps?: TooltipGetter;
};

// Full-registry SVGs carry hardcoded fills (e.g. action/done.svg: fill="#172B4D"); the old
// foundation Icon overrides them with `path {fill: currentColor}` so the BE color applies,
// EXCEPT for these inherently multicolor categories, which must keep their own fills.
const MULTICOLOR_ICON_PREFIXES =
  /^(?:flags|flagsCurrencies|files_compact|files_default|condition|automotive)\//;

// The full registry's `fallback` entry is a raw svg URL (string), not a component; the tw Icon
// renders registry values (including the fallback during lazy load), so it must be dropped.
const iconRegistry = omit(['fallback'], themeIcons);

// Own-property check: `in` would also accept inherited keys (constructor, __proto__, …) from
// BE data, which the tw Icon's getSafely lookup rejects by throwing outside its error boundary.
const isRegisteredIcon = (value: string | undefined): value is string =>
  isNotNil(value) && Object.hasOwn(iconRegistry, value);

export function IconCellRenderer(props: IconCellRendererProps) {
  const className = getCellClassName(props.isWrap);
  const tooltipProps = getTooltipProps(props.row, props.getTooltipProps);

  const style = getStylesToRender<CellStyle>(props);
  const iconType = style?.icon?.type;
  const mappedColor = getMappedIconColor(style?.icon?.color);
  // BE sends arbitrary icon keys (e.g. carPassIcon: action/done, action/hourglass_empty);
  // the tw foundation registry only carries a handful, so resolve against the complete
  // platform/foundation registry and fall back to plain text for keys it doesn't know —
  // same behavior as the old datagrid's isThemeIconKey check.
  const isValidIcon = isRegisteredIcon(iconType) && isNotNil(mappedColor);

  if (!isValidIcon) {
    return (
      <Tooltip {...tooltipProps}>
        <div className={className}>{props.value}</div>
      </Tooltip>
    );
  }

  return (
    <Tooltip {...tooltipProps}>
      <div className={className}>
        <HStack spacing={1} align="center">
          <span
            className={
              MULTICOLOR_ICON_PREFIXES.test(iconType)
                ? 'contents'
                : 'contents [&_path]:fill-current'
            }
          >
            <Icon<string>
              value={iconType}
              color={mappedColor}
              size={4}
              iconRegistry={iconRegistry}
            />
          </span>
          <Text color={style?.textColor} size="small" noWrap={!props.isWrap}>
            {props.value}
          </Text>
        </HStack>
      </div>
    </Tooltip>
  );
}
